import React from 'react';
import { Alert, Box } from '@mui/material';

/**
 * BulkActionToolbar - A reusable toolbar that renders action buttons with eligibility counts.
 *
 * @param {Object} props
 * @param {Array} props.actions - Action configurations array
 * @param {Array} props.selectedItems - Array of selected item objects
 * @param {boolean} props.loading - Whether a bulk operation is in progress
 * @param {Object} [props.progress] - { processed: number, total: number }
 * @param {Object} [props.summary] - { successCount: number, failedCount: number, errors: Array }
 * @param {Function} [props.onDismissSummary] - Callback to dismiss the summary
 */
const BulkActionToolbar = ({ actions, selectedItems, loading, progress, summary, onDismissSummary }) => {
  return (
    <div className="mb-3">
      {/* Summary Alert */}
      {summary && (
        <Box sx={{ mb: 2, width: '100%' }}>
          <Alert
            severity={summary.failedCount > 0 ? 'warning' : 'success'}
            onClose={onDismissSummary}
            aria-live="polite"
          >
            {summary.successCount} succeeded, {summary.failedCount} failed
          </Alert>
        </Box>
      )}

      {/* Progress Indicator */}
      {loading && progress && (
        <div className="d-flex align-items-center mb-2">
          <div className="spinner-border spinner-border-sm me-2" role="status" aria-label="Processing">
            <span className="visually-hidden">Processing...</span>
          </div>
          <span>Processing {progress.processed} of {progress.total}...</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex flex-wrap gap-2">
        {actions.map((action) => {
          const eligibleCount = selectedItems.filter(action.eligibilityFilter).length;
          const isDisabled = eligibleCount === 0 || loading === true;

          return (
            <button
              key={action.name}
              type="button"
              className={`btn btn-${action.variant}`}
              disabled={isDisabled}
              onClick={() => action.onAction(selectedItems.filter(action.eligibilityFilter))}
            >
              {action.icon && <i className={`${action.icon} me-1`}></i>}
              {action.label} {eligibleCount} of {selectedItems.length}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BulkActionToolbar;
